import { useCallback, useRef, useState, type ReactNode } from 'react';
import { Box, HStack, Text, VStack } from '@chakra-ui/react';
import { FaCamera, FaImage, FaTrash } from 'react-icons/fa';
import { useAuth } from '../hooks/useAuth';
import {
  deleteProfileImage,
  resizeProfileImage,
  uploadProfileImage,
  type ProfileImageType,
} from '../utils/profileImage';
import { pageBg } from '../theme/themeColor';
import { RiftboundButton } from './ui/button';
import { RiftboundPlayerAvatar } from './RiftboundPlayerAvatar';
import { riftboundToaster } from './ui/toaster';

export interface BannerAvatarEditorProps {
  name: string;
  /** Persisted custom avatar URL (controls remove + R2 cleanup). */
  avatarUrl: string | null | undefined;
  bannerUrl: string | null | undefined;
  /**
   * Optional preview URL when it can differ from `avatarUrl`
   * (e.g. Google account photo fallback on the player profile).
   */
  displayAvatarUrl?: string | null | undefined;
  /** Persist the new avatar URL (null = cleared). */
  onAvatarChange: (url: string | null) => void | Promise<void>;
  /** Persist the new banner/background URL (null = cleared). */
  onBannerChange: (url: string | null) => void | Promise<void>;
  disabled?: boolean;
  emptyBannerLabel?: string;
  removeAvatarLabel?: string;
  /** Line under the display name (e.g. @handle or TAG). */
  subtitle?: ReactNode;
  /** Extra content under subtitle (badges, actions, etc.). */
  details?: ReactNode;
  /** Avatar and name only — used by the new-player prompt, which has no banner step. */
  hideBanner?: boolean;
  /** Large centered avatar, no name row. For short setup steps. */
  centered?: boolean;
}

/**
 * Shared banner + overlapping avatar picker used by player profile and team forms.
 * Uploads through `/api/profile` (R2), then calls the parent change handlers.
 */
export function BannerAvatarEditor({
  name,
  avatarUrl,
  bannerUrl,
  displayAvatarUrl,
  onAvatarChange,
  onBannerChange,
  disabled = false,
  emptyBannerLabel = 'Add a background image',
  removeAvatarLabel = 'Remove avatar',
  subtitle,
  details,
  hideBanner = false,
  centered = false,
}: BannerAvatarEditorProps) {
  const { getIdToken } = useAuth();
  const avatarInputRef = useRef<HTMLInputElement | null>(null);
  const bannerInputRef = useRef<HTMLInputElement | null>(null);
  const [avatarSaving, setAvatarSaving] = useState(false);
  const [bannerSaving, setBannerSaving] = useState(false);

  const banner = bannerUrl?.trim() || null;
  const avatar = avatarUrl?.trim() || null;
  const previewAvatar = displayAvatarUrl?.trim() || avatar;

  const upload = useCallback(
    async (type: ProfileImageType, file: File | null) => {
      if (!file || disabled) return;
      const setSaving = type === 'avatar' ? setAvatarSaving : setBannerSaving;
      const oldUrl = type === 'avatar' ? avatar : banner;
      const persist = type === 'avatar' ? onAvatarChange : onBannerChange;
      let idToken: string | null = null;
      let uploadedUrl: string | null = null;
      setSaving(true);
      try {
        idToken = await getIdToken();
        if (!idToken) throw new Error('Sign in again to upload an image.');
        const blob = await resizeProfileImage(file, type);
        uploadedUrl = await uploadProfileImage({ type, blob, idToken, oldUrl });
        await persist(uploadedUrl);
        if (oldUrl && oldUrl !== uploadedUrl) {
          await deleteProfileImage({ type, idToken, url: oldUrl }).catch(() => {});
        }
      } catch (err) {
        riftboundToaster.create({
          title: 'Upload failed',
          description: err instanceof Error ? err.message : 'Failed to upload image.',
          type: 'error',
        });
        if (idToken && uploadedUrl) {
          await deleteProfileImage({ type, idToken, url: uploadedUrl }).catch(() => {});
        }
      } finally {
        setSaving(false);
        if (type === 'avatar' && avatarInputRef.current) avatarInputRef.current.value = '';
        if (type === 'background' && bannerInputRef.current) bannerInputRef.current.value = '';
      }
    },
    [avatar, banner, disabled, getIdToken, onAvatarChange, onBannerChange]
  );

  const remove = useCallback(
    async (type: ProfileImageType) => {
      if (disabled) return;
      const oldUrl = type === 'avatar' ? avatar : banner;
      if (!oldUrl) return;
      const setSaving = type === 'avatar' ? setAvatarSaving : setBannerSaving;
      const persist = type === 'avatar' ? onAvatarChange : onBannerChange;
      setSaving(true);
      try {
        await persist(null);
        const idToken = await getIdToken();
        if (idToken) {
          await deleteProfileImage({ type, idToken, url: oldUrl }).catch(() => {});
        }
      } catch (err) {
        riftboundToaster.create({
          title: 'Error',
          description: err instanceof Error ? err.message : 'Failed to remove image.',
          type: 'error',
        });
      } finally {
        setSaving(false);
      }
    },
    [avatar, banner, disabled, getIdToken, onAvatarChange, onBannerChange]
  );

  const avatarCluster = (
    <HStack gap={4} p={hideBanner ? 0 : 4} align="flex-start">
      <Box position="relative" flexShrink={0} mt={hideBanner ? 0 : '-52px'}>
        <RiftboundPlayerAvatar
          name={name || 'Team'}
          photoURL={previewAvatar}
          size="2xl"
          colorPalette="purple"
          border="4px solid"
          borderColor="bgElevated"
        />
        <RiftboundButton
          aria-label="Change avatar"
          title="Change avatar"
          size="xs"
          treatment="primary"
          minW="0"
          px={2}
          borderRadius="full"
          position="absolute"
          right="-2px"
          bottom="-2px"
          onClick={() => avatarInputRef.current?.click()}
          loading={avatarSaving}
          disabled={disabled}
        >
          <FaCamera />
        </RiftboundButton>
      </Box>
      <Box flex={1} minW={0}>
        <Text fontWeight="semibold" fontSize="lg" truncate>
          {name || '(no name set)'}
        </Text>
        {subtitle ? (
          <Box fontSize="sm" color="textMuted">
            {subtitle}
          </Box>
        ) : null}
        {(avatar || details) ? (
          <HStack gap={2} mt={2} wrap="wrap">
            {details}
            {avatar ? (
              <RiftboundButton
                size="xs"
                treatment="ghost"
                onClick={() => void remove('avatar')}
                loading={avatarSaving}
                disabled={disabled}
              >
                {removeAvatarLabel}
              </RiftboundButton>
            ) : null}
          </HStack>
        ) : null}
      </Box>
    </HStack>
  );

  if (hideBanner && centered) {
    return (
      <VStack gap={3} align="center" w="full">
        <input
          ref={avatarInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          hidden
          onChange={(e) => void upload('avatar', e.currentTarget.files?.[0] ?? null)}
        />
        <Box position="relative">
          <RiftboundPlayerAvatar
            name={name || 'Player'}
            photoURL={previewAvatar}
            size="2xl"
            colorPalette="purple"
            boxSize="120px"
            border="4px solid"
            borderColor="bgElevated"
          />
          <RiftboundButton
            aria-label={previewAvatar ? 'Change photo' : 'Add a photo'}
            title={previewAvatar ? 'Change photo' : 'Add a photo'}
            size="sm"
            treatment="primary"
            minW="0"
            px={2}
            borderRadius="full"
            position="absolute"
            right="2px"
            bottom="2px"
            onClick={() => avatarInputRef.current?.click()}
            loading={avatarSaving}
            disabled={disabled}
          >
            <FaCamera />
          </RiftboundButton>
        </Box>
        {avatar ? (
          <RiftboundButton
            size="xs"
            treatment="ghost"
            onClick={() => void remove('avatar')}
            loading={avatarSaving}
            disabled={disabled}
          >
            {removeAvatarLabel}
          </RiftboundButton>
        ) : null}
      </VStack>
    );
  }

  if (hideBanner) {
    return (
      <Box>
        <input
          ref={avatarInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          hidden
          onChange={(e) => void upload('avatar', e.currentTarget.files?.[0] ?? null)}
        />
        {avatarCluster}
      </Box>
    );
  }

  return (
    <Box
      border="hairline"
      borderColor="border"
      borderRadius="xl"
      overflow="hidden"
      bg="bgElevated"
    >
      <input
        ref={avatarInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        hidden
        onChange={(e) => void upload('avatar', e.currentTarget.files?.[0] ?? null)}
      />
      <input
        ref={bannerInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        hidden
        onChange={(e) => void upload('background', e.currentTarget.files?.[0] ?? null)}
      />

      <Box
        position="relative"
        minH={{ base: '132px', md: '180px' }}
        bg={banner ? 'transparent' : 'purple.950'}
        backgroundImage={
          banner
            ? `linear-gradient(180deg, ${pageBg(0.2)}, ${pageBg(0.84)}), url(${banner})`
            : undefined
        }
        backgroundSize="cover"
        backgroundPosition="center"
      >
        {!banner && (
          <VStack
            position="absolute"
            inset={0}
            justify="center"
            color="textMuted"
            gap={2}
            pointerEvents="none"
            px={4}
            textAlign="center"
          >
            <FaImage />
            <Text fontSize="sm">{emptyBannerLabel}</Text>
          </VStack>
        )}
        <HStack
          gap={2}
          position="absolute"
          top={3}
          right={3}
          zIndex={1}
          maxW="calc(100% - 1.5rem)"
          justify="flex-end"
          flexWrap="wrap"
        >
          <RiftboundButton
            size="xs"
            treatment="secondary"
            onClick={() => bannerInputRef.current?.click()}
            loading={bannerSaving}
            disabled={disabled}
          >
            <FaCamera />
            {banner ? 'Change banner' : 'Add banner'}
          </RiftboundButton>
          {banner ? (
            <RiftboundButton
              size="xs"
              treatment="ghost"
              onClick={() => void remove('background')}
              loading={bannerSaving}
              disabled={disabled}
            >
              <FaTrash />
              Remove
            </RiftboundButton>
          ) : null}
        </HStack>
      </Box>

      {avatarCluster}
    </Box>
  );
}
